import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, ChevronDown, Flame } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', hasDropdown: true },
    { name: 'About', path: '/why-choose-us' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Bar - Minimal */}
      <div className={`transition-all duration-500 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'}`}>
        <div className="h-full bg-black flex items-center justify-center">
          <p className="text-xs text-white/80 tracking-widest uppercase">
            <span className="text-lime-400 font-bold">Free Shipping</span> on orders $75+ • USA Only • {CONTACT_INFO.phone}
          </p>
        </div>
      </div>

      {/* Main Header - Glassmorphism */}
      <nav className={`transition-all duration-500 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-xl py-3 shadow-2xl shadow-lime-500/5' 
          : 'bg-black/90 backdrop-blur-md py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            
            {/* Left - Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.slice(0, 3).map((link) => (
                <div key={link.name} className="relative">
                  {link.hasDropdown ? (
                    <button
                      onClick={() => setShowCategories(!showCategories)}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium tracking-wide transition-all rounded-full ${
                        location.pathname.includes('/shop')
                          ? 'text-lime-400'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-4 py-2 text-sm font-medium tracking-wide transition-all rounded-full ${
                        location.pathname === link.path
                          ? 'text-lime-400'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                  
                  {/* Dropdown */}
                  {link.hasDropdown && showCategories && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCategories(false)} />
                      <div className="absolute top-full left-0 mt-2 w-56 bg-black/95 backdrop-blur-xl rounded-2xl border border-white/10 py-2 z-20 shadow-2xl">
                        <Link
                          to="/shop"
                          className="block px-4 py-2.5 text-sm text-white hover:text-lime-400 hover:bg-white/5 font-medium"
                        >
                          All Products
                        </Link>
                        <div className="h-px bg-white/10 my-1" />
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/shop?category=${cat.slug}`}
                            className="block px-4 py-2 text-sm text-white/70 hover:text-lime-400 hover:bg-white/5 transition-colors"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Center - Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="hidden sm:block">
                <span className="text-white font-black text-2xl tracking-tight">
                  ACTIVE<span className="text-lime-400">ONYX</span> FIT
                </span>
              </div>
              <div className="sm:hidden">
                <span className="text-white font-black text-lg tracking-tight">
                  AO<span className="text-lime-400">FIT</span>
                </span>
              </div>
            </Link>

            {/* Right - Navigation + Actions */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.slice(3).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium tracking-wide transition-all rounded-full ${
                    location.pathname === link.path
                      ? 'text-lime-400'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="w-px h-5 bg-white/20 mx-2" />
              
              {/* Auth */}
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="p-2 text-white/70 hover:text-white transition-colors"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              ) : (
                <Link
                  to="/login"
                  className="p-2 text-white/70 hover:text-white transition-colors"
                >
                  <User className="w-5 h-5" />
                </Link>
              )}

              {/* Cart */}
              <button
                onClick={openCart}
                className="relative ml-2 p-3 bg-lime-400 hover:bg-lime-300 rounded-xl text-black transition-all group"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-black text-lime-400 text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-lime-400">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="flex lg:hidden items-center gap-3">
              <button
                onClick={openCart}
                className="relative p-2.5 bg-lime-400 rounded-xl text-black"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-black text-lime-400 text-[10px] font-bold flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-white"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-lime-400/10 text-lime-400'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/10 mt-4">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-white/70 hover:text-white rounded-xl transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white rounded-xl transition-colors"
                >
                  <User className="w-5 h-5" />
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
